import React, { useEffect, useMemo, useState } from "react";
import { workoutPlanApi } from "../api/routes";
import { Button, Card, Input, Select, TextArea, Toast, TwoColumn } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const distanceOptions = [
  { label: "5K", value: "FIVE_K" },
  { label: "10K", value: "TEN_K" },
  { label: "Half Marathon", value: "HALF_MARATHON" },
  { label: "Marathon", value: "MARATHON" },
  { label: "Ultra Marathon", value: "ULTRA_MARATHON" },
  { label: "Off Season", value: "OFF_SEASON" },
];

const blankPlan = {
  userId: "",
  planId: "",
  name: "",
  philosophyId: "",
  goal: { name: "", race: { name: "", url: "", distance: "" }, targetTime: "", pace: "", date: "" },
  startDate: "",
  status: "",
  weeks: [],
  planSummary: "",
  createdAt: "",
  planVersion: 0,
  planEmbeddingVersion: 0,
};

const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const parseLocalDate = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const toIsoDate = (date) => {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const formatDateLabel = (value, options = { month: "short", day: "numeric" }) => {
  const date = parseLocalDate(value);
  return date ? date.toLocaleDateString(undefined, options) : "";
};

const sourceUnitLabel = (unit) => {
  if (unit === "METER") return "m";
  if (unit === "KILOMETER") return "km";
  if (unit === "MILE") return "mi";
  return "km";
};

const distanceLabel = (distance) => {
  if (!distance) return null;
  if (typeof distance !== "object") return `${distance} km`;
  if (distance.label) return distance.label;
  if (distance.value !== undefined && distance.value !== null) return `${distance.value} ${distance.unit || "km"}`;
  if (distance.sourceValue !== undefined && distance.sourceValue !== null) return `${distance.sourceValue} ${sourceUnitLabel(distance.sourceUnit)}`;
  if (distance.meters !== undefined && distance.meters !== null) return `${Number((distance.meters / 1000).toFixed(2))} km`;
  return null;
};

const dateForWorkoutDay = (weekStartValue, day) => {
  const weekStart = parseLocalDate(weekStartValue);
  if (!weekStart || !day) return "";
  const targetIndex = dayNames.findIndex((name) => name.toLowerCase().startsWith(String(day).slice(0, 3).toLowerCase()));
  if (targetIndex < 0) return "";
  const offset = (targetIndex - weekStart.getDay() + 7) % 7;
  return toIsoDate(addDays(weekStart, offset));
};

const weekDateRange = (planStartDate, weekIndex) => {
  const start = parseLocalDate(planStartDate);
  if (!start) return { startDate: "", endDate: "" };
  const weekStart = addDays(start, weekIndex * 7);
  return { startDate: toIsoDate(weekStart), endDate: toIsoDate(addDays(weekStart, 6)) };
};

const normalizeWorkoutLabel = (workout) => {
  if (!workout) return "Workout";
  if (typeof workout === "string") return workout;
  return workout.name || workout.type || workout.workoutId || "Workout";
};

const buildCalendarDays = (plan, selectedWeekIndex, weekDetail) => {
  const week = plan?.weeks?.[selectedWeekIndex];
  if (!week) return [];
  const range = weekDateRange(plan.startDate, selectedWeekIndex);
  const startDate = week.startDate || weekDetail?.weekStartDate || range.startDate;
  const sourceDays = Array.isArray(weekDetail?.days) && weekDetail.days.length > 0
    ? weekDetail.days.map((day) => ({
        day: day.day,
        date: day.date || dateForWorkoutDay(startDate, day.day),
        workouts: day.workouts || [],
      }))
    : Object.entries(week.workouts || {}).map(([day, workoutIds]) => ({
        day,
        date: week.workoutDates?.[day] || dateForWorkoutDay(startDate, day),
        workouts: workoutIds || [],
      }));

  return sourceDays.sort((a, b) => {
    const aIndex = dayOrder.findIndex((day) => day.toLowerCase().startsWith(String(a.day).slice(0, 3).toLowerCase()));
    const bIndex = dayOrder.findIndex((day) => day.toLowerCase().startsWith(String(b.day).slice(0, 3).toLowerCase()));
    return (aIndex < 0 ? 99 : aIndex) - (bIndex < 0 ? 99 : bIndex);
  });
};

export default function WorkoutPlansPanel() {
  const [plan, setPlan] = useState(blankPlan);
  const [userIdLookup, setUserIdLookup] = useState("");
  const [planLookup, setPlanLookup] = useState("");
  const [mode, setMode] = useState("search");
  const [panelState, setPanelState] = usePanelCache("plans");
  const results = panelState.results || [];
  const showSearch = panelState.showSearch ?? true;
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [searching, setSearching] = useState(false);
  const [calendarMode, setCalendarMode] = useState("week");
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState(toIsoDate(new Date()));
  const [weekDetail, setWeekDetail] = useState(null);
  const [weekLoading, setWeekLoading] = useState(false);

  const showToast = (message, tone = "success") => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2500);
  };

  const loadPlan = (data = blankPlan) => {
    const nextPlan = { ...blankPlan, ...data, weeks: data?.weeks || [] };
    setPlan(nextPlan);
    const today = toIsoDate(new Date());
    const planStart = parseLocalDate(nextPlan.startDate);
    if (planStart && nextPlan.weeks?.length) {
      const daysSinceStart = Math.floor((parseLocalDate(today) - planStart) / 86400000);
      const clampedWeek = Math.min(Math.max(Math.floor(daysSinceStart / 7), 0), nextPlan.weeks.length - 1);
      const nextWeekIndex = Number.isFinite(clampedWeek) ? clampedWeek : 0;
      const nextRange = weekDateRange(nextPlan.startDate, nextWeekIndex);
      setSelectedWeekIndex(nextWeekIndex);
      setSelectedDate(today >= nextRange.startDate && today <= nextRange.endDate ? today : nextRange.startDate);
    } else {
      setSelectedWeekIndex(0);
    }
    setWeekDetail(null);
  };

  const calendarDays = useMemo(
    () => buildCalendarDays(plan, selectedWeekIndex, weekDetail),
    [plan, selectedWeekIndex, weekDetail]
  );

  const selectedDay = useMemo(() => {
    if (!calendarDays.length) return null;
    return calendarDays.find((day) => day.date === selectedDate) || calendarDays[0];
  }, [calendarDays, selectedDate]);

  useEffect(() => {
    let cancelled = false;
    if (!plan.userId || !plan.weeks?.length) {
      setWeekDetail(null);
      return undefined;
    }
    setWeekLoading(true);
    workoutPlanApi.week(plan.userId, selectedWeekIndex)
      .then((res) => {
        if (!cancelled) setWeekDetail(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setWeekDetail(null);
      })
      .finally(() => {
        if (!cancelled) setWeekLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [plan.userId, plan.weeks, selectedWeekIndex]);

  const buildCreatePayload = () => ({
    userId: plan.userId,
    name: plan.name,
    philosophyId: plan.philosophyId,
    goal: plan.goal,
    startDate: plan.startDate,
  });

  // For update, send the entire plan object we have in state so we don't drop fields like weeks/summary/status.
  const buildUpdatePayload = () => ({
    ...plan,
    weeks: plan.weeks || [],
    goal: plan.goal || {},
  });

  const handleCreate = async () => {
    if (!plan.userId || !plan.name || !plan.philosophyId) {
      showToast("userId, name, and philosophyId are required", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await workoutPlanApi.create(buildCreatePayload());
      const created = res.data || buildCreatePayload();
      setPanelState({ results: [created, ...results] });
      loadPlan({ userId: plan.userId, philosophyId: plan.philosophyId });
      setMode("search");
    } catch (e) {
      showToast(e.displayMessage || e.message || "Error creating plan", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!plan.planId) {
      showToast("planId required", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await workoutPlanApi.update(plan.planId, buildUpdatePayload());
      const updated = res.data || { ...buildUpdatePayload(), planId: plan.planId };
      setPanelState({
        results: (results || []).map((p) => (p.planId === plan.planId ? { ...p, ...updated } : p)),
      });
      setMode("search");
    } catch (e) {
      showToast(e.displayMessage || e.message || "Error updating plan", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!planId) return;
    setSaving(true);
    try {
      await workoutPlanApi.delete(planId);
      setPanelState({ results: (results || []).filter((p) => p.planId !== planId) });
      setMode("search");
      showToast("Plan deleted");
    } catch (e) {
      showToast(e.displayMessage || e.message || "Error deleting plan", "error");
    } finally {
      setSaving(false);
    }
  };

  const fetchByUser = async () => {
    if (!userIdLookup.trim()) {
      showToast("Enter a user ID before searching", "error");
      return;
    }
    setSearching(true);
    try {
      const res = await workoutPlanApi.byUser(userIdLookup);
      setPanelState({ results: res.data || [], showSearch: false });
    } catch (e) {
      setPanelState({ results: [] });
      showToast(e.displayMessage || e.message || "Error fetching plans", "error");
    } finally {
      setSearching(false);
    }
  };

  const fetchCurrent = async () => {
    if (!userIdLookup.trim()) {
      showToast("Enter a user ID before searching", "error");
      return;
    }
    setSearching(true);
    try {
      const res = await workoutPlanApi.current(userIdLookup);
      loadPlan(res.data);
      setPanelState({ results: res.data ? [res.data] : [], showSearch: false });
    } catch (e) {
      setPanelState({ results: [] });
      showToast(e.displayMessage || e.message || "Error fetching current plan", "error");
    } finally {
      setSearching(false);
    }
  };

  const fetchPlan = async () => {
    if (!planLookup.trim()) {
      showToast("Enter a plan ID before searching", "error");
      return;
    }
    setSearching(true);
    try {
      const res = await workoutPlanApi.byPlanId(planLookup);
      loadPlan(res.data);
      setPanelState({ results: res.data ? [res.data] : [], showSearch: false });
    } catch (e) {
      setPanelState({ results: [] });
      showToast(e.displayMessage || e.message || "Error fetching plan", "error");
    } finally {
      setSearching(false);
    }
  };

  const renderSearch = () => (
    <Card
      title="Workout plans"
      action={
        <div style={{ display: "flex", gap: 8 }}>
          <Button tone="ghost" onClick={() => setPanelState({ showSearch: !showSearch })}>{showSearch ? "Hide search" : "Show search"}</Button>
          <Button onClick={() => { loadPlan(blankPlan); setMode("edit"); }}>+ New plan</Button>
        </div>
      }
    >
      {showSearch && (
        <Card title="Search">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
            <Input label="User ID" value={userIdLookup} onChange={setUserIdLookup} />
            <Input label="Plan ID" value={planLookup} onChange={setPlanLookup} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, marginTop: 6 }}>
            <Button onClick={fetchByUser} disabled={saving || !userIdLookup.trim()} loading={searching}>Search by user</Button>
            <Button tone="secondary" onClick={fetchCurrent} disabled={saving || !userIdLookup.trim()} loading={searching}>Current plan</Button>
            <Button tone="secondary" onClick={fetchPlan} disabled={saving || !planLookup.trim()} loading={searching}>Search by plan</Button>
          </div>
        </Card>
      )}
      {results.length === 0 ? (
        <div style={{ padding: 12, color: "var(--stone-700)" }}>No results yet. Run a search.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
          <thead>
            <tr style={{ background: "rgba(25,164,99,0.08)" }}>
              <th style={{ padding: 8, textAlign: "left" }}>Name</th>
              <th style={{ padding: 8, textAlign: "left" }}>Goal Distance</th>
              <th style={{ padding: 8, textAlign: "left" }}>Goal Pace</th>
              <th style={{ padding: 8, textAlign: "left" }}>Plan ID</th>
              <th style={{ padding: 8, textAlign: "left" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {results.map((p) => (
              <tr key={p.planId} style={{ borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
                <td style={{ padding: 8, cursor: "pointer" }} onClick={() => { loadPlan(p); setMode("edit"); }}>{p.name}</td>
                <td style={{ padding: 8 }}>{p.goal?.race?.distance}</td>
                <td style={{ padding: 8 }}>{p.goal?.pace}</td>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{p.planId}</td>
                <td style={{ padding: 8 }}>
                  <Button tone="ghost" onClick={() => handleDelete(p.planId)} disabled={saving}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );

  const renderWorkoutCalendar = () => {
    if (!plan.weeks || plan.weeks.length === 0) return null;
    const range = weekDateRange(plan.startDate, selectedWeekIndex);
    const activeRangeStart = weekDetail?.weekStartDate || plan.weeks[selectedWeekIndex]?.startDate || range.startDate;
    const activeRangeEnd = weekDetail?.weekEndDate || plan.weeks[selectedWeekIndex]?.endDate || range.endDate;
    const today = toIsoDate(new Date());

    return (
      <Card
        title="Training calendar"
        tone="accent"
        action={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={selectedWeekIndex}
              onChange={(e) => {
                setSelectedWeekIndex(Number(e.target.value));
                setWeekDetail(null);
              }}
              style={{
                border: "1px solid rgba(15,23,42,0.12)",
                borderRadius: 10,
                padding: "8px 10px",
                fontWeight: 700,
                background: "white",
              }}
            >
              {plan.weeks.map((week, index) => (
                <option key={`week-option-${index}`} value={index}>
                  Week {week.weekNumber || index + 1}
                </option>
              ))}
            </select>
            <div style={{ display: "inline-flex", padding: 3, borderRadius: 12, background: "rgba(15,23,42,0.06)" }}>
              {["week", "day"].map((view) => (
                <button
                  key={view}
                  onClick={() => setCalendarMode(view)}
                  style={{
                    border: "none",
                    borderRadius: 9,
                    padding: "7px 12px",
                    background: calendarMode === view ? "#0f172a" : "transparent",
                    color: calendarMode === view ? "white" : "#334155",
                    fontWeight: 800,
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {view}
                </button>
              ))}
            </div>
          </div>
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 14,
          }}
        >
          <div>
            <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: 0 }}>
              {formatDateLabel(activeRangeStart)} - {formatDateLabel(activeRangeEnd)}
            </div>
            <div style={{ color: "var(--stone-700)", fontWeight: 700 }}>
              {weekLoading ? "Loading workout details..." : `${calendarDays.length} scheduled training days`}
            </div>
          </div>
          <Button tone="ghost" onClick={() => {
            const currentWeek = plan.weeks.findIndex((week) => {
              const start = week.startDate || weekDateRange(plan.startDate, plan.weeks.indexOf(week)).startDate;
              const end = week.endDate || weekDateRange(plan.startDate, plan.weeks.indexOf(week)).endDate;
              return today >= start && today <= end;
            });
            if (currentWeek >= 0) setSelectedWeekIndex(currentWeek);
            setSelectedDate(today);
          }}>
            Today
          </Button>
        </div>

        {calendarMode === "week" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
            {calendarDays.map((day) => {
              const isToday = day.date === today;
              const isSelected = day.date === selectedDate;
              const workouts = day.workouts || [];
              return (
                <button
                  key={`${day.day}-${day.date}`}
                  onClick={() => {
                    setSelectedDate(day.date);
                    setCalendarMode("day");
                  }}
                  style={{
                    textAlign: "left",
                    padding: 14,
                    borderRadius: 12,
                    border: isSelected ? "2px solid #19a463" : "1px solid rgba(15,23,42,0.08)",
                    background: isToday ? "linear-gradient(145deg, #f0fdf4, #ffffff)" : "white",
                    boxShadow: isSelected ? "0 14px 32px rgba(25,164,99,0.18)" : "0 8px 22px rgba(15,23,42,0.06)",
                    cursor: "pointer",
                    minHeight: 160,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 900 }}>{day.day}</div>
                      <div style={{ color: "var(--stone-700)", fontSize: 13, fontWeight: 700 }}>
                        {formatDateLabel(day.date)}
                      </div>
                    </div>
                    {isToday && (
                      <span style={{ fontSize: 11, fontWeight: 900, color: "#0c3b2e", background: "rgba(25,164,99,0.14)", borderRadius: 999, padding: "4px 8px", height: 22 }}>
                        Today
                      </span>
                    )}
                  </div>
                  <div style={{ display: "grid", gap: 7 }}>
                    {workouts.length === 0 ? (
                      <div style={{ color: "var(--stone-600)", fontWeight: 700 }}>Rest</div>
                    ) : workouts.map((workout, index) => (
                      <div
                        key={`${day.day}-${index}`}
                        style={{
                          padding: "8px 9px",
                          borderRadius: 9,
                          background: "rgba(15,23,42,0.04)",
                          color: "#0f172a",
                          fontWeight: 800,
                          fontSize: 13,
                          lineHeight: 1.25,
                        }}
                      >
                        {normalizeWorkoutLabel(workout)}
                      </div>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              borderRadius: 14,
              border: "1px solid rgba(15,23,42,0.08)",
              background: "linear-gradient(145deg, #ffffff, #f8fafc)",
              padding: 18,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 900 }}>{selectedDay?.day || "Training day"}</div>
                <div style={{ color: "var(--stone-700)", fontWeight: 700 }}>
                  {selectedDay?.date ? formatDateLabel(selectedDay.date, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "No date"}
                </div>
              </div>
              <select
                value={selectedDay?.date || ""}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  border: "1px solid rgba(15,23,42,0.12)",
                  borderRadius: 10,
                  padding: "8px 10px",
                  fontWeight: 700,
                  background: "white",
                  alignSelf: "flex-start",
                }}
              >
                {calendarDays.map((day) => (
                  <option key={`${day.day}-${day.date}-select`} value={day.date}>
                    {day.day} {formatDateLabel(day.date)}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {(selectedDay?.workouts || []).length === 0 ? (
                <div style={{ padding: 16, borderRadius: 12, background: "rgba(15,23,42,0.04)", fontWeight: 800 }}>
                  Rest day
                </div>
              ) : selectedDay.workouts.map((workout, index) => (
                <div
                  key={`selected-workout-${index}`}
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: "white",
                    border: "1px solid rgba(15,23,42,0.08)",
                    boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: 16 }}>{normalizeWorkoutLabel(workout)}</div>
                  {workout?.estimatedDuration && <div style={{ color: "var(--stone-700)", marginTop: 4 }}>Duration: {workout.estimatedDuration}</div>}
                  {distanceLabel(workout?.estimatedDistance) ? <div style={{ color: "var(--stone-700)", marginTop: 4 }}>Distance: {distanceLabel(workout.estimatedDistance)}</div> : null}
                  {workout?.notes && <div style={{ color: "var(--stone-700)", marginTop: 8, lineHeight: 1.45 }}>{workout.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderEdit = () => (
    <Card
      title={plan.planId ? "Edit workout plan" : "Create workout plan"}
      action={<Button tone="ghost" onClick={() => setMode("search")}>← Back to search</Button>}
    >
      {plan.planId && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Plan ID</div>
          <div style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(15,23,42,0.03)", fontWeight: 700 }}>
            {plan.planId}
          </div>
        </div>
      )}
      {(plan.planVersion || plan.planEmbeddingVersion || plan.status || plan.createdAt) && (
        <div style={{ marginBottom: 12, color: "var(--stone-700)", fontSize: 13, lineHeight: 1.5 }}>
          {plan.status && <div>Status: {plan.status}</div>}
          {plan.createdAt && <div>Created: {plan.createdAt}</div>}
          {plan.planVersion ? <div>Plan version: {plan.planVersion}</div> : null}
          {plan.planEmbeddingVersion ? <div>Embedding version: {plan.planEmbeddingVersion}</div> : null}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px,1fr))", gap: 12 }}>
        <Input label="User ID" value={plan.userId} onChange={(v) => setPlan({ ...plan, userId: v })} />
        <Input label="Name" value={plan.name} onChange={(v) => setPlan({ ...plan, name: v })} />
        <Input label="Philosophy ID" value={plan.philosophyId} onChange={(v) => setPlan({ ...plan, philosophyId: v })} />
        <Input label="Start Date" value={plan.startDate} onChange={(v) => setPlan({ ...plan, startDate: v })} />
      </div>
      <Card title="Goal" tone="accent">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
          <Input label="Name" value={plan.goal?.name || ""} onChange={(v) => setPlan({ ...plan, goal: { ...(plan.goal || {}), name: v } })} />
          <Input
            label="Race"
            value={plan.goal?.race?.name || ""}
            onChange={(v) =>
              setPlan({ ...plan, goal: { ...(plan.goal || {}), race: { ...(plan.goal?.race || {}), name: v } } })
            }
          />
          <Input
            label="Race URL"
            value={plan.goal?.race?.url || ""}
            onChange={(v) =>
              setPlan({ ...plan, goal: { ...(plan.goal || {}), race: { ...(plan.goal?.race || {}), url: v } } })
            }
          />
          <Select
            label="Distance"
            value={plan.goal?.race?.distance || ""}
            onChange={(v) =>
              setPlan({ ...plan, goal: { ...(plan.goal || {}), race: { ...(plan.goal?.race || {}), distance: v } } })
            }
            options={distanceOptions}
          />
          <Input label="Pace" value={plan.goal?.pace || ""} onChange={(v) => setPlan({ ...plan, goal: { ...(plan.goal || {}), pace: v } })} />
          <Input label="Target Time" value={plan.goal?.targetTime || ""} onChange={(v) => setPlan({ ...plan, goal: { ...(plan.goal || {}), targetTime: v } })} />
          <Input label="Date" value={plan.goal?.date || ""} onChange={(v) => setPlan({ ...plan, goal: { ...(plan.goal || {}), date: v } })} />
        </div>
      </Card>
      {renderWorkoutCalendar()}
      {plan.planSummary && (
        <TextArea
          label="LLM plan summary (read-only)"
          value={plan.planSummary}
          onChange={() => {}}
        />
      )}
      {plan.weeks && plan.weeks.length > 0 && (
        <TextArea
          label="Weeks (read-only, set by LLM)"
          value={JSON.stringify(plan.weeks, null, 2)}
          onChange={() => {}}
        />
      )}
      <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
        {!plan.planId && (
          <Button onClick={handleCreate} loading={saving}>Create</Button>
        )}
        {plan.planId && (
          <>
            <Button tone="secondary" onClick={handleUpdate} loading={saving}>Update</Button>
            <Button tone="ghost" onClick={() => handleDelete(plan.planId)} disabled={saving}>
              Delete
            </Button>
          </>
        )}
      </div>
    </Card>
  );

  return (
    <>
      <TwoColumn left={mode === "search" ? renderSearch() : renderEdit()} right={<div />} />
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </>
  );
}
